import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { http, Field } from '@api';
import { getRowValues, formatCell, isDateField, isAmountField, isNumericField } from './seachpage-helpers';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import { getAccessTokenSilent } from '../auth/msal';

export interface ActionMenuProps {
  doc: any;
  openMenuId: number | null;
  setOpenMenuId: (id: number | null) => void;
  onEdit: (doc: any) => void;
  onAudit: (doc: any) => void;
  onDelete: (doc: any) => Promise<void> | void;
  fields: Field[];
  resultColumns: string[];
  selectedIds?: number[];
  visibleIds?: number[];
}

export const ActionMenu: React.FC<ActionMenuProps> = ({ doc, openMenuId, setOpenMenuId, onEdit, onAudit, onDelete, fields, resultColumns, selectedIds, visibleIds }) => {
  const isOpen = openMenuId === doc.id;
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);
  const [fileProgress, setFileProgress] = useState<{ index: number; total: number } | null>(null);
  const [lastFileName, setLastFileName] = useState<string | null>(null);

  function updatePosition() {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setCoords({ top: rect.bottom + 4, left: rect.right });
  }

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (containerRef.current && containerRef.current.contains(target)) return;
      if (anchorRef.current && anchorRef.current.contains(target)) return;
      // Prevent the click that closed the menu from also triggering row expansion in the grid.
      try { (window as any).__suppressNextRowClick = true; } catch {}
      // Clear the flag shortly after in case it isn't consumed.
      setTimeout(() => { try { delete (window as any).__suppressNextRowClick; } catch {} }, 500);
      setOpenMenuId(null);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, setOpenMenuId]);

  // Also suppress the next row click when the menu closes for any reason (keyboard, programmatic)
  const prevOpenRef = useRef<boolean>(false);
  useEffect(() => {
    if (prevOpenRef.current && !isOpen) {
      try { (window as any).__suppressNextRowClick = true; } catch {}
      setTimeout(() => { try { delete (window as any).__suppressNextRowClick; } catch {} }, 500);
    }
    prevOpenRef.current = isOpen;
  }, [isOpen]);

  // Update position when opening and on scroll/resize
  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [isOpen]);

  const handleKey = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') { action(); setOpenMenuId(null); }
    if (e.key === 'Escape') setOpenMenuId(null);
  };

  function displayTitle(fld: Field | undefined, key: string) {
    if (fld?.title) return fld.title;
    const k = (key || '').toLowerCase();
    if (k === 'description' || k === 'document_description' || k === 'documentdescription') return 'Description';
    if (k === 'createdate' || k === 'documentdatecreated') return 'Create Date';
    if (k === 'documentid' || k === 'id' || k === 'docid') return 'Document Id';
    if (k === 'subfolder') return 'Subfolder';
    if (k === 'subfolder' ) return 'Subfolder';
    return key;
  }

  // Create a real xlsx using SheetJS
  async function downloadExcelForSearch() {
    try {
      const params = new URLSearchParams(window.location.search || '');
      const drawerId = params.get('drawerid');
      if (!drawerId) { alert('Drawer not known for search'); return; }

      // Build filter from URL (same as SearchPage)
      const filterParts: string[] = [];
      filterParts.push(`drawer eq ${drawerId}`);
      params.forEach((v, k) => {
        const key = k.toLowerCase();
        if (key === 'drawerid') return;
        const dotIdx = v.indexOf('.');
        if (dotIdx > 0) {
          const op = v.substring(0, dotIdx);
          const val = v.substring(dotIdx + 1);
          if (op === 'empty' || op === 'notempty') { filterParts.push(`and ${key} ${op}`); }
          else filterParts.push(`and ${key} ${op} ${val}`);
        } else {
          filterParts.push(`and ${key} eq ${v}`);
        }
      });
      const filter = filterParts.join(' ');

      let items: any[] = [];
      // Priority: selectedIds > visibleIds > full search
      if (selectedIds && selectedIds.length > 0) {
        const ids = selectedIds.map(n => Number(n)).filter(n => !isNaN(n));
        const docs = await Promise.all(ids.map(async (id) => { try { return await http(`/api/document/${id}`); } catch { return null; } }));
        items = (docs.filter(d => d) as any[]);
      } else if (visibleIds && visibleIds.length > 0) {
        const ids = visibleIds.map(n => Number(n)).filter(n => !isNaN(n));
        const docs = await Promise.all(ids.map(async (id) => { try { return await http(`/api/document/${id}`); } catch { return null; } }));
        items = (docs.filter(d => d) as any[]);
      } else {
        const data = await http('/api/document/search', { method: 'POST', json: { filter, take: 10000 } });
        items = data.Values ?? data.values ?? [];
      }
      if (!items || items.length === 0) { alert('No results to export'); return; }

      // Determine columns order: prefer resultColumns from grid, fallback to fields-derived order
      const cols = (resultColumns && resultColumns.length) ? resultColumns : (() => {
        const fieldNames = fields.map(f=>f.name);
        const folderNames = fields.filter(f=>f.folderfield).map(f=>f.name);
        const docNames = fields.filter(f=>!f.folderfield).map(f=>f.name);
        const ordered: string[] = [];
        folderNames.forEach(n => { if (!ordered.includes(n)) ordered.push(n); });
        if (!ordered.includes('subfolder')) ordered.push('subfolder');
        docNames.forEach(n => { if (!ordered.includes(n)) ordered.push(n); });
        ['documentid','createdate'].forEach(m => { if (!ordered.includes(m)) ordered.push(m); });
        return ordered;
      })();

      const headers = cols.map(c => displayTitle(fields.find(f=>f.name===c), c));

      // Build worksheet data as array of arrays (rows)
      const wsData: any[][] = [];
      wsData.push(headers);

      for (const d of items) {
        const row: any[] = [];
        for (const col of cols) {
          const fld = fields.find(f=>f.name===col);
          const raw = getRowValues(d, [col])[0];
          let formatted: any = formatCell(fld, raw);
          if ((!fld && col.toLowerCase() === 'createdate') && typeof raw === 'string') {
            const dt = new Date(raw);
            if (!isNaN(dt.getTime())) formatted = dt.toLocaleString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
          }
          // Try to pass proper types for XLSX: numbers and dates
          if (isAmountField(fld) || isNumericField(fld)) {
            const num = Number((formatted ?? '').toString().replace(',', '.'));
            row.push(isNaN(num) ? formatted : num);
          } else if ((fld && fld.datatype && (fld.datatype.toLowerCase()==='date' || fld.datatype.toLowerCase()==='datetime')) || (!fld && col.toLowerCase()==='createdate')) {
            const dt = new Date(raw);
            row.push(!isNaN(dt.getTime()) ? dt : formatted);
          } else {
            row.push(formatted);
          }
        }
        wsData.push(row);
      }

      const ws = XLSX.utils.aoa_to_sheet(wsData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Export');

      // Generate binary and trigger download
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      a.href = url;
      a.download = `search-export-${drawerId}-${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

    } catch (e:any) {
      alert('Export failed: ' + (e?.message || e));
    }
  }

  // Download ZIP of all PDFs in search
  async function downloadAllPdfsForSearch() {
    try {
      console.log('[downloadAllPdfsForSearch] start');
      const params = new URLSearchParams(window.location.search || '');
      const drawerId = params.get('drawerid');
      console.log('[downloadAllPdfsForSearch] parsed drawerId=', drawerId);
      if (!drawerId) { alert('Drawer not known for search'); console.log('[downloadAllPdfsForSearch] abort: no drawerId'); return; }

      // Build filter string from URL params (same format as SearchPage)
      let filter = `drawer eq ${drawerId}`;
      params.forEach((v, k) => {
        const key = k.toLowerCase();
        if (key === 'drawerid') return;
        const dotIdx = v.indexOf('.');
        if (dotIdx > 0) {
          const op = v.substring(0, dotIdx);
          const val = v.substring(dotIdx + 1);
          if (op === 'empty' || op === 'notempty') filter += ` and ${key} ${op}`;
          else filter += ` and ${key} ${op} ${val}`;
        } else {
          filter += ` and ${key} eq ${v}`;
        }
      });

      console.log('[downloadAllPdfsForSearch] filter=', filter);

      // Client-side zip: either use selectedIds if provided or perform search to get items
      setDownloading(true);
      setDownloadProgress(0);
      setFileProgress(null);
      try {
        const idsToDownload: number[] = [];
        if (selectedIds && selectedIds.length > 0) {
          for (const s of selectedIds) { const n = Number(s); if (!isNaN(n)) idsToDownload.push(n); }
        } else if (visibleIds && visibleIds.length > 0) {
          for (const s of visibleIds) { const n = Number(s); if (!isNaN(n)) idsToDownload.push(n); }
        } else {
          const data = await http('/api/document/search', { method: 'POST', json: { filter, take: 10000 } });
          const items = data.Values ?? data.values ?? [];
          if (!items || items.length === 0) { alert('No documents to download'); return; }
          for (const it of items) { const id = Number(it.id ?? it.Id); if (!isNaN(id)) idsToDownload.push(id); }
        }

        if (idsToDownload.length === 0) { alert('No documents to download'); return; }

        const zip = new JSZip();
        // get token for API file download
        const token = await getAccessTokenSilent();
        if (!token) throw new Error('Not authenticated');

        // parallel downloads with limited concurrency
        const concurrency = 4;
        let nextIndex = 0;
        let completed = 0;

        const total = idsToDownload.length;
        setFileProgress({ index: 0, total });

        async function worker() {
          while (true) {
            const idx = nextIndex++;
            if (idx >= idsToDownload.length) break;
            const id = idsToDownload[idx];
            setFileProgress({ index: idx + 1, total });
            try {
              const fileRes = await fetch(`/api/document/${id}/file`, { headers: { Authorization: `Bearer ${token}` } });
              if (!fileRes.ok) { console.warn('failed to fetch file from API', id, fileRes.status); }
              else {
                // filename from header
                let name: string | null = null;
                const cd = fileRes.headers.get('content-disposition');
                if (cd) {
                  const m = /filename\*=UTF-8''([^;\n]+)/i.exec(cd) || /filename="?([^";\n]+)"?/i.exec(cd);
                  if (m && m[1]) { try { name = decodeURIComponent(m[1]); } catch { name = m[1]; } }
                }
                if (!name) name = `${id}.pdf`;
                const safeName = name.replace(/[\\/:*?"<>|]/g, '_');
                setLastFileName(safeName);
                const ab = await fileRes.arrayBuffer();
                zip.file(safeName, ab);
              }
            } catch (ex) {
              console.warn('error downloading doc', id, ex);
            }
            completed++;
            setDownloadProgress(Math.round((completed / total) * 90));
          }
        }

        const workers = [] as Promise<void>[];
        for (let i = 0; i < Math.min(concurrency, idsToDownload.length); i++) workers.push(worker());
        await Promise.all(workers);

        // zip generation weight = 10%
        const content = await zip.generateAsync({ type: 'blob' }, (meta: { percent: number; currentFile?: string }) => {
          setDownloadProgress(Math.round(90 + meta.percent * 0.1));
        });

        const url = URL.createObjectURL(content);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'documents.zip';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } finally {
        setDownloading(false);
        setTimeout(()=> setDownloadProgress(null), 300);
        setFileProgress(null);
      }

    } catch (e:any) {
      console.error('[downloadAllPdfsForSearch] error', e);
      alert('Download failed: ' + (e?.message || e));
      setDownloading(false);
      setDownloadProgress(null);
      setFileProgress(null);
    }
  }

  async function handleDelete() {
    try {
      const ok = window.confirm('Delete this document?');
      if (!ok) return;
      await onDelete(doc);
    } catch (e: any) {
      alert('Delete failed: ' + (e?.message || e));
    } finally {
      setOpenMenuId(null);
    }
  }

  async function handleDeleteSelected() {
    try {
      const ids = (selectedIds || []).map(n => Number(n)).filter(n => !isNaN(n));
      // if there is no selection (or only this doc), just fall back to single delete
      if (ids.length <= 1) {
        await handleDelete();
        return;
      }

      const ok = window.confirm(`Delete ${ids.length} selected document(s)?`);
      if (!ok) return;

      // Delete sequentially to keep load predictable and make it easier to surface failing id
      for (const id of ids) {
        await onDelete({ id });
      }
    } catch (e: any) {
      alert('Bulk delete failed: ' + (e?.message || e));
    } finally {
      setOpenMenuId(null);
    }
  }

  return (
    <div className="relative">
      <button
        ref={anchorRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label="Actions"
        onClick={(e) => { e.stopPropagation(); setOpenMenuId(isOpen ? null : doc.id); }}
        className="doc-button icon-button"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="8" cy="3" r="1.6" fill="#555" />
          <circle cx="8" cy="8" r="1.6" fill="#555" />
          <circle cx="8" cy="13" r="1.6" fill="#555" />
        </svg>
      </button>
      {isOpen && coords && ReactDOM.createPortal(
        <div ref={containerRef} className="action-menu-panel" style={{ top: coords.top, left: coords.left }}>
          <ul className="action-menu" role="menu">
            <li
              role="menuitem"
              tabIndex={0}
              className="action-menu-item"
              onClick={(e) => { e.stopPropagation(); onEdit(doc); setOpenMenuId(null); }}
              onKeyDown={(e) => { e.stopPropagation(); handleKey(e, () => onEdit(doc)); }}
            >
              Edit properties
            </li>
              <li
                role="menuitem"
                tabIndex={0}
                className="action-menu-item"
                onClick={(e) => { e.stopPropagation(); handleDelete(); }}
                onKeyDown={(e) => { e.stopPropagation(); handleKey(e, () => handleDelete()); }}
              >
                Delete document
              </li>

              {selectedIds && selectedIds.length > 1 && (
                <li
                  role="menuitem"
                  tabIndex={0}
                  className="action-menu-item"
                  onClick={(e) => { e.stopPropagation(); handleDeleteSelected(); }}
                  onKeyDown={(e) => { e.stopPropagation(); handleKey(e, () => handleDeleteSelected()); }}
                >
                  Delete selected documents ({selectedIds.length})
                </li>
              )}
            <li
              role="menuitem"
              tabIndex={0}
              className="action-menu-item"
              onClick={(e) => { e.stopPropagation(); onAudit(doc); setOpenMenuId(null); }}
              onKeyDown={(e) => { e.stopPropagation(); handleKey(e, () => onAudit(doc)); }}
            >
              View audit
            </li>
            <li
              role="menuitem"
              tabIndex={0}
              className="action-menu-item"
              onClick={(e) => { e.stopPropagation(); downloadAllPdfsForSearch(); setOpenMenuId(null); }}
              onKeyDown={(e) => { e.stopPropagation(); handleKey(e, () => downloadAllPdfsForSearch()); }}
            >
              Download (ZIP)
            </li>
            <li
              role="menuitem"
              tabIndex={0}
              className="action-menu-item"
              onClick={(e) => { e.stopPropagation(); downloadExcelForSearch(); setOpenMenuId(null); }}
              onKeyDown={(e) => { e.stopPropagation(); handleKey(e, () => downloadExcelForSearch()); }}
            >
              Export to Excel (XLSX)
            </li>
          </ul>
        </div>, document.body)
      } 
      {downloading && ReactDOM.createPortal(
        <div style={{ position:'fixed', inset:0, display:'flex', alignItems:'center', justifyContent:'center', zIndex:12000 }}>
          <div style={{ background:'rgba(0,0,0,0.6)', padding:20, borderRadius:8, color:'#fff', minWidth:320, textAlign:'center' }}>
            <div style={{ marginBottom:12, fontWeight:600 }}>Downloading files...</div>
            <div style={{ height:12, background:'#333', borderRadius:6, overflow:'hidden', marginBottom:8 }}>
              <div style={{ height:'100%', width: (downloadProgress != null ? `${downloadProgress}%` : '100%'), background:'#4caf50', transition:'width 0.2s' }} />
            </div>
            <div style={{ fontSize:12 }}>{downloadProgress != null ? `${downloadProgress}%` : (fileProgress? `Downloading ${fileProgress.index} of ${fileProgress.total}` : 'Downloading�')}</div>
            {lastFileName && <div style={{ fontSize:10, marginTop:8 }}>{lastFileName}</div>}
          </div>
        </div>, document.body)
      }
    </div>
  );
};

export default ActionMenu;
